import { Trash2 } from 'lucide-react';

import type { EditableVariable } from './editorTypes';
import { STRUCTURAL_EDIT_LOCK_REASON } from './questionEditRestrictions';
import styles from './VariablesPanel.module.css';

interface VariablesPanelProps {
  detectedVariableNames: string[];
  disabled?: boolean;
  onAddVariable: () => void;
  onRemoveVariable: (index: number) => void;
  onUpdateVariable: (
    index: number,
    updates: Partial<Pick<EditableVariable, 'maxValue' | 'minValue' | 'name'>>,
  ) => void;
  structuralEditsRestricted?: boolean;
  variables: EditableVariable[];
}

export function VariablesPanel({
  detectedVariableNames,
  disabled = false,
  onAddVariable,
  onRemoveVariable,
  onUpdateVariable,
  structuralEditsRestricted = false,
  variables,
}: VariablesPanelProps) {
  const structuralDisabled = disabled || structuralEditsRestricted;
  const lockReason = structuralEditsRestricted
    ? STRUCTURAL_EDIT_LOCK_REASON
    : undefined;

  return (
    <section className={styles.root}>
      <div aria-live="polite" className={styles.detected}>
        <span className={styles.detectedLabel}>Detected</span>
        <div className={styles.chips}>
          {detectedVariableNames.length > 0 ? (
            detectedVariableNames.map((name) => (
              <span className={styles.chip} key={name}>
                {name}
              </span>
            ))
          ) : (
            <span className={styles.emptyText}>
              No <code>##variable##</code> references yet.
            </span>
          )}
        </div>
      </div>

      {variables.length > 0 ? (
        <div className={styles.table}>
          {/* One header for the whole table instead of a label trio per row.
              Rows carry their own aria-labels so a screen reader still hears
              which field is which without the visible repetition. */}
          <div aria-hidden="true" className={styles.tableHeader}>
            <span>Name</span>
            <span>Min</span>
            <span>Max</span>
            <span />
          </div>

          <ul className={styles.rows}>
            {variables.map((variable, index) => (
              <li className={styles.row} key={variable.id}>
                <input
                  aria-label={`Variable ${index + 1} name`}
                  className={styles.input}
                  disabled={structuralDisabled}
                  onChange={(event) => {
                    onUpdateVariable(index, { name: event.target.value });
                  }}
                  placeholder="name"
                  title={lockReason}
                  type="text"
                  value={variable.name}
                />
                <input
                  aria-label={`Variable ${index + 1} minimum`}
                  className={styles.input}
                  disabled={structuralDisabled}
                  onChange={(event) => {
                    onUpdateVariable(index, {
                      minValue:
                        event.target.value === ''
                          ? undefined
                          : Number(event.target.value),
                    });
                  }}
                  step="any"
                  title={lockReason}
                  type="number"
                  value={variable.minValue ?? ''}
                />
                <input
                  aria-label={`Variable ${index + 1} maximum`}
                  className={styles.input}
                  disabled={structuralDisabled}
                  onChange={(event) => {
                    onUpdateVariable(index, {
                      maxValue:
                        event.target.value === ''
                          ? undefined
                          : Number(event.target.value),
                    });
                  }}
                  step="any"
                  title={lockReason}
                  type="number"
                  value={variable.maxValue ?? ''}
                />
                <button
                  aria-label={`Remove variable ${index + 1}`}
                  className={styles.removeButton}
                  disabled={structuralDisabled}
                  onClick={() => {
                    onRemoveVariable(index);
                  }}
                  title={lockReason}
                  type="button"
                >
                  <Trash2 aria-hidden="true" size={15} strokeWidth={2} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <button
        className={styles.addButton}
        disabled={structuralDisabled}
        onClick={onAddVariable}
        title={lockReason}
        type="button"
      >
        Add variable
      </button>
    </section>
  );
}
